import { DocumentSummary, DocumentTypeConfig } from "@/types/document";
import { Page, PageParams } from "@/types/pagination";

import { requestData } from "@/api/api-config";

export async function getPatientDocuments(
  patientId: string,
  params?: PageParams & { docType?: string },
): Promise<Page<DocumentSummary> | null> {
  const { docType, ...rest } = params ?? {};
  return await requestData<Page<DocumentSummary>>(
    `user/patient/${patientId}/documents`,
    "get",
    {
      searchParams: { ...rest, ...(docType ? { doc_type: docType } : {}) },
    },
  );
}

export async function uploadDocument(
  patientId: string,
  file: File,
  title: string,
  docType: string,
  summary?: string,
  expiryDate?: string | null,
): Promise<DocumentSummary | null> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("patient_id", patientId);
  formData.append("title", title);
  formData.append("doc_type", docType);

  if (summary) {
    formData.append("summary", summary);
  }

  if (expiryDate) {
    formData.append("expiry_date", expiryDate);
  }

  return await requestData<DocumentSummary>(
    "user/patient/document/upload",
    "post",
    {
      body: formData,
    },
    false,
  );
}

export async function setDocumentExpiry(
  documentId: string,
  expiryDate: string | null,
): Promise<DocumentSummary | null> {
  return await requestData<DocumentSummary>(
    `user/patient/document/${documentId}/expiry`,
    "put",
    { json: { expiryDate } },
    false,
  );
}

export async function deleteDocument(
  documentId: string,
): Promise<boolean | null> {
  return await requestData<boolean>(
    `user/patient/document/${documentId}`,
    "delete",
  );
}

export async function updateDocument(
  documentId: string,
  title: string,
  docType: string,
): Promise<DocumentSummary | null> {
  return await requestData<DocumentSummary>(
    `user/patient/document/${documentId}`,
    "put",
    { json: { title, docType } },
    false,
  );
}

export async function signOffDocument(
  documentId: string,
  comment?: string,
): Promise<DocumentSummary | null> {
  return await requestData<DocumentSummary>(
    `user/patient/document/${documentId}/sign-off`,
    "post",
    { json: { comment: comment ?? null } },
    false,
  );
}

export async function updateDocumentReviewComment(
  documentId: string,
  comment: string,
): Promise<DocumentSummary | null> {
  return await requestData<DocumentSummary>(
    `user/patient/document/${documentId}/review-comment`,
    "patch",
    { json: { comment } },
    false,
  );
}

export async function shareDocumentWithPatient(
  documentId: string,
  shared: boolean,
): Promise<DocumentSummary | null> {
  return await requestData<DocumentSummary>(
    `user/patient/document/${documentId}/share`,
    "patch",
    { json: { shared } },
    false,
  );
}

export async function sendDocumentText(
  documentId: string,
  message: string,
): Promise<DocumentSummary | null> {
  return await requestData<DocumentSummary>(
    `user/patient/document/${documentId}/send-text`,
    "post",
    { json: { message } },
    false,
  );
}

export async function getDocumentTypes(): Promise<DocumentTypeConfig[] | null> {
  return await requestData<DocumentTypeConfig[]>(
    "user/patient/document-types",
    "get",
  );
}

export async function getAdminDocumentTypes(): Promise<
  DocumentTypeConfig[] | null
> {
  return await requestData<DocumentTypeConfig[]>("admin/document-types", "get");
}

export async function createDocumentType(
  name: string,
): Promise<DocumentTypeConfig | null> {
  return await requestData<DocumentTypeConfig>("admin/document-types", "post", {
    json: { name },
  });
}

export async function updateDocumentType(
  id: string,
  name: string,
): Promise<DocumentTypeConfig | null> {
  return await requestData<DocumentTypeConfig>(
    `admin/document-types/${id}`,
    "put",
    { json: { name } },
  );
}

export async function deleteDocumentType(id: string): Promise<void | null> {
  return await requestData<void>(`admin/document-types/${id}`, "delete");
}
